// Voice AI Pulse vs Project Health
//
// Each entry anchors to an existing project (grant) in `grants.js` so the
// aggregate project metrics (score, partner, ₹ figures) stay consistent with
// the Projects Overview tables. The Voice AI side is a single beneficiary
// sample — its sentiment may or may not align with the aggregate project health,
// which is precisely the tension this widget surfaces.

import { GRANTS, enrichGrant, ragForScore } from "./grants";
import { PARTNERS } from "./partners";

// Helper: derive partner-level KPI completion (% already enriched on partners.js)
const partnerKpi = (partnerId) =>
  PARTNERS.find((p) => p.id === partnerId)?.kpiCompletion ?? 70;

// signal = quadrant the (project-health × VAI-sentiment) pair lands in
function deriveSignal(score, sentiment) {
  const strong = score >= 4.0;
  if (strong && sentiment === "positive")  return "aligned_positive";   // green
  if (strong && sentiment === "negative")  return "early_warning";      // amber
  if (!strong && sentiment === "positive") return "resilience";         // blue
  if (!strong && sentiment === "negative") return "confirms_concern";   // red
  return "neutral";                                                     // grey
}

const RAW = [
  // 1 — Aligned positive (strong + positive)
  {
    id: "vp-1",
    grantId: "YBF-G-014",
    beneficiary: { name: "Mahua Soren", initials: "MS", location: "Jharkhand · Dumka district", capturedOn: "Oct 2024" },
    sentiment: "positive",
    excerpt:
      "Earlier, I sold rice at whatever the trader offered. Today our SHG has its own godown and we sell direct — my income has doubled and my daughter is in school.",
    insight:
      "Aggregate metrics validated on the ground — beneficiary directly attributes income lift to the programme.",
  },

  // 2 — Aligned positive (strong + positive)
  {
    id: "vp-2",
    grantId: "YBF-G-023",
    beneficiary: { name: "Birendra Patil", initials: "BP", location: "Maharashtra · Ahmednagar", capturedOn: "Jul 2024" },
    sentiment: "positive",
    excerpt:
      "We planted 1,200 native trees on our farm. Crop yield up 35%, and groundwater table has risen by 2.4 metres. The soil feels alive again.",
    insight:
      "Strong project score corroborated by quantified ground-level outcomes from the beneficiary.",
  },

  // 3 — Early-warning signal (strong + negative) — KEY DEMO MOMENT
  {
    id: "vp-3",
    grantId: "YBF-G-001",
    beneficiary: { name: "Trina Das", initials: "TD", location: "West Bengal · Kolkata", capturedOn: "Aug 2024" },
    sentiment: "negative",
    excerpt:
      "The training was excellent, but the 1.5-hour commute is exhausting. I'm worried I won't keep this job after the rains start.",
    insight:
      "Project scoring 4.6/5 — yet Voice AI surfaced a retention risk surveys would miss. Flag for partner follow-up on commute support.",
  },

  // 4 — Early-warning signal (strong + negative)
  {
    id: "vp-4",
    grantId: "YBF-G-016",
    beneficiary: { name: "Hari Singh", initials: "HS", location: "Rajasthan · Alwar", capturedOn: "Sep 2024" },
    sentiment: "negative",
    excerpt:
      "The johad is rebuilt — water is good. But the panchayat is now charging us for cattle watering. Some of us are excluded from the very work we did.",
    insight:
      "Strong infrastructure outcomes mask a governance / access issue. Partner should review post-handover protocols.",
  },

  // 5 — Resilience signal (at-risk + positive) — KEY DEMO MOMENT
  {
    id: "vp-5",
    grantId: "YBF-G-009",
    beneficiary: { name: "Pinky Vaghela", initials: "PV", location: "Gujarat · Ahmedabad", capturedOn: "Oct 2024" },
    sentiment: "positive",
    excerpt:
      "I was the first in my family to complete Class 10. The mentor calls every week — she's the reason I'm sitting for entrance exams next month.",
    insight:
      "Project score is at-risk (2.8/5) but the mentorship model is producing transformative individual outcomes. Worth scaling the mentor cadence.",
  },

  // 6 — Confirms concern (at-risk + negative)
  {
    id: "vp-6",
    grantId: "YBF-G-028",
    beneficiary: { name: "Suresh Gowda", initials: "SG", location: "Karnataka · Tumakuru", capturedOn: "Aug 2024" },
    sentiment: "negative",
    excerpt:
      "Saplings were planted but no one came back to check on them. Half of them died in the dry months — I water the ones I can reach.",
    insight:
      "At-risk score corroborated by ground-level failure mode. Post-plantation monitoring and watering plan needs urgent redesign.",
  },
];

// Compose the final list with enriched project info
export const VOICE_VS_PROJECT = RAW.map((item) => {
  const g = enrichGrant(GRANTS.find((x) => x.id === item.grantId));
  return {
    ...item,
    project: {
      id: g.id,
      name: g.name,
      partner: g.partnerName,
      partnerId: g.partnerId,
      pillar: g.pillar,
      score: g.score,
      scoreRag: ragForScore(g.score),
      status: g.status,
      sanctionedCr: g.fundedCr,
      utilisedCr: g.utilisedCr,
      utilisePct: g.utilisePct,
      kpiCompletion: partnerKpi(g.partnerId),
    },
    signal: deriveSignal(g.score, item.sentiment),
  };
});

export const SENTIMENT_META = {
  positive: { label: "Positive", tone: "green", emoji: "👍", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "#16A34A" },
  neutral:  { label: "Neutral",  tone: "slate", emoji: "😐", chip: "bg-slate-50  text-slate-700  border-slate-200",   dot: "#64748B" },
  negative: { label: "Negative", tone: "rose",  emoji: "👎", chip: "bg-rose-50   text-rose-700   border-rose-200",    dot: "#DC2626" },
};

export const SIGNAL_META = {
  aligned_positive:  { label: "Aggregate validated",  tone: "green",  fill: "#16A34A", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  early_warning:     { label: "Early-warning signal", tone: "amber",  fill: "#D97706", chip: "bg-amber-50 text-amber-700 border-amber-200" },
  resilience:        { label: "Resilience signal",    tone: "blue",   fill: "#2563EB", chip: "bg-blue-50 text-blue-700 border-blue-200" },
  confirms_concern:  { label: "Confirms concern",     tone: "rose",   fill: "#DC2626", chip: "bg-rose-50 text-rose-700 border-rose-200" },
  neutral:           { label: "Neutral signal",       tone: "slate",  fill: "#64748B", chip: "bg-slate-50 text-slate-700 border-slate-200" },
};
